/**
 * @file spreadsheetUtils.ts
 * @description Utility functions for handling spreadsheet data import and mapping.
 * This file contains helper functions to validate and process imported spreadsheet data.
 */

import { ContentDetails } from "../../types";

/**
 * Validates and maps the imported spreadsheet data to the application's data structure.
 * @param mappedData - The data mapped from the spreadsheet columns to application fields.
 * @param columnDefs - The column definitions from AG Grid.
 * @returns An array of validated ContentDetails objects.
 */
export const mapSpreadsheetData = (
  mappedData: Partial<ContentDetails>[],
  columnDefs: any[]
): ContentDetails[] => {
  return mappedData
    .filter((row) =>
      Object.values(row).some((value) => {
        if (typeof value === "string") {
          return value.trim() !== "";
        }
        return value !== undefined && value !== null;
      })
    )
    .map((row) => {
      const validatedRow: Partial<ContentDetails> = {};
      columnDefs.forEach((colDef) => {
        const field = colDef.field as keyof ContentDetails;
        if (
          field !== "status" &&
          field !== "website" &&
          field !== "clientInfo" &&
          row[field] !== undefined &&
          row[field] !== null
        ) {
          if (field === "relatedKeywords" || field === "peopleAlsoAsk") {
            let items: string[] = [];
            const fieldValue: unknown = row[field];
            if (typeof fieldValue === "string") {
              // Support both comma-separated and newline-separated values
              // Try comma first, then newline if no commas found
              const trimmedValue = fieldValue.trim();
              if (trimmedValue.includes(",")) {
                items = trimmedValue
                  .split(",")
                  .map((item: string) => item.trim())
                  .filter(Boolean);
              } else if (trimmedValue.includes("\n") || trimmedValue.includes("\r\n")) {
                items = trimmedValue
                  .split(/\r?\n/)
                  .map((item: string) => item.trim())
                  .filter(Boolean);
              } else if (trimmedValue) {
                // Single value
                items = [trimmedValue];
              }
            } else if (Array.isArray(fieldValue)) {
              items = fieldValue
                .map((item: unknown) => String(item).trim())
                .filter(Boolean);
            }
            validatedRow[field] = items;
          } else if (typeof row[field] === "string") {
            const trimmedValue = (row[field] as string).trim();
            if (trimmedValue !== "") {
              (validatedRow as any)[field] = trimmedValue;
            }
          } else if (typeof row[field] === "boolean") {
            (validatedRow as any)[field] = row[field];
          }
        }
      });

      // Restituisci null se la riga validata è vuota
      // Note: website and clientInfo are excluded - they come from plan-level presets
      const rowWithLegacy = validatedRow as Partial<ContentDetails> & { topic?: string; audience?: string };
      return Object.keys(validatedRow).length > 0
        ? ({
            contentDetailId: validatedRow.contentDetailId || "",
            website: "", // Will be populated from plan-level preset
            contentType: validatedRow.contentType || rowWithLegacy.topic || "",
            clientInfo: "", // Will be populated from plan-level preset
            keyword: validatedRow.keyword || "",
            relatedKeywords: Array.isArray(validatedRow.relatedKeywords)
              ? validatedRow.relatedKeywords
              : [],
            peopleAlsoAsk: Array.isArray(validatedRow.peopleAlsoAsk)
              ? validatedRow.peopleAlsoAsk
              : [],
            title: validatedRow.title || "",
            status: validatedRow.status,
            downloaded: validatedRow.downloaded,
          } as ContentDetails)
        : null;
    })
    .filter((row): row is ContentDetails => row !== null);
};

/**
 * Generates column mappings based on similarity between sheet columns and app columns.
 * @param sheetColumns - The column names from the imported spreadsheet.
 * @param appColumns - The column names used in the application.
 * @returns An object with suggested mappings from sheet columns to app columns.
 */
export const generateColumnMappings = (
  sheetColumns: string[],
  appColumns: string[]
): Record<string, string> => {
  const autoMappings = autoMapColumns(sheetColumns);
  const mappings: Record<string, string> = { ...autoMappings };

  sheetColumns.forEach((sheetColumn) => {
    if (!mappings[sheetColumn]) {
      const normalizedSheetColumn = sheetColumn
        .toLowerCase()
        .replace(/\s+/g, "");
      const matchingAppColumn = appColumns.find(
        (appColumn) =>
          appColumn.toLowerCase().replace(/\s+/g, "") === normalizedSheetColumn
      );
      if (matchingAppColumn) {
        mappings[sheetColumn] = matchingAppColumn;
      }
    }
  });

  return mappings;
};

export const autoMapColumns = (
  sheetColumns: string[]
): Record<string, string> => {
  const columnMappings: Record<string, string[]> = {
    // website and clientInfo are excluded - they come from plan-level presets
    contentType: ["tipo contenuto", "content type", "contenttype", "tipo di contenuto", "topic", "ambito di riferimento"],
    keyword: [
      "keyword principale",
      "keyword target",
      "main keyword",
      "primary keyword",
      "target keyword",
      "keyword",
    ],
    relatedKeywords: [
      "keyword secondarie",
      "related keywords",
      "secondary keywords",
    ],
    peopleAlsoAsk: [
      "people also ask",
      "paa",
      "domande frequenti",
      "faq",
      "domande",
    ],
    title: [
      "titolo",
      "title",
      "titolo di riferimento",
      "reference title",
    ],
  };

  const mappings: Record<string, string> = {};
  sheetColumns.forEach((sheetColumn) => {
    const normalizedSheetColumn = sheetColumn.toLowerCase().trim();
    for (const [appColumn, possibleNames] of Object.entries(columnMappings)) {
      if (possibleNames.includes(normalizedSheetColumn)) {
        mappings[sheetColumn] = appColumn;
        break;
      }
    }
  });

  return mappings;
};
